/**
 * documentUsage — Documents_On_Site Prompt 6.
 *
 * Pure helpers that surface doc-report linkage across the platform:
 *
 *   - `summarizeSiteDocs` powers the Overview tab counts widget.
 *   - `topRelevanceByReportType` powers the per-type top-hits list.
 *   - `applicableReportTypes` powers the DocumentLibrary "Applicable to"
 *     badge (intrinsic, classifier-driven — sourced from `doc.relevance`).
 *   - `usedInReportTypes` powers the audit-trail "Cited by" lookup
 *     (extrinsic, generation-history-driven — sourced from
 *     `report.documentRefs[].userIncluded`).
 *   - `documentBasisRow` powers the ComparisonView "Document basis" row.
 *
 * Side-effect free so the visibility code can be unit-tested without
 * mounting React or the store.
 */
import type { SiteDocument, SiteRagReport } from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import type { ReportTypeId } from '../config/reportTypes';
import { isLibraryIndexDoc } from './knowledge/libraryIndexDocument';
import { getReportDocuments } from './reportDocs';
import { computeBodyCitedDocIds } from './reportCitedDocIds';

const TOP_HITS_PER_TYPE = 5;
const HOVER_PREVIEW_LIMIT = 3;
const RELEVANCE_DISPLAY_THRESHOLD = 0.3;

/* ── Site-wide doc summary ──────────────────────────────────────── */

export interface SiteDocsSummary {
  total: number;
  nbPriority: number;
  classified: number;
  unclassified: number;
}

/**
 * Counts user docs (excluding the auto-generated library-index doc).
 * `classified` = at least one numeric entry in `relevance`. `unclassified`
 * = docs the relevance classifier hasn't scored yet (legacy uploads or a
 * post-Prompt-2 admin reclassify still pending).
 */
export function summarizeSiteDocs(site: { documents?: SiteDocument[] } | undefined): SiteDocsSummary {
  const docs = (site?.documents ?? []).filter(d => !isLibraryIndexDoc(d));
  let nbPriority = 0;
  let classified = 0;
  for (const d of docs) {
    if (d.priority) nbPriority++;
    if (d.relevance && Object.values(d.relevance).some(v => typeof v === 'number')) classified++;
  }
  return {
    total: docs.length,
    nbPriority,
    classified,
    unclassified: docs.length - classified,
  };
}

/* ── Top relevance by report type ─────────────────────────────── */

export interface TopRelevanceEntry {
  reportType: ReportTypeId;
  docs: Array<{ doc: SiteDocument; score: number }>;
  highestRelevance: number;
  totalDocsForType: number;
}

/**
 * For each ReportTypeId, returns up to `TOP_HITS_PER_TYPE` docs whose
 * relevance for that type is ≥ 0.3, sorted highest-first. The Overview
 * widget renders the resulting list with `highestRelevance` as the
 * leading score in the section header. Empty arrays are filtered out
 * so report types nobody has docs for don't render an empty bullet.
 */
export function topRelevanceByReportType(
  site: { documents?: SiteDocument[] } | undefined,
  reportTypes: ReportTypeId[],
): TopRelevanceEntry[] {
  const docs = (site?.documents ?? []).filter(d => !isLibraryIndexDoc(d));
  const out: TopRelevanceEntry[] = [];
  for (const reportType of reportTypes) {
    const scored: Array<{ doc: SiteDocument; score: number }> = [];
    for (const d of docs) {
      const score = d.relevance?.[reportType];
      if (typeof score === 'number' && score >= RELEVANCE_DISPLAY_THRESHOLD) {
        scored.push({ doc: d, score });
      }
    }
    if (scored.length === 0) continue;
    scored.sort((a, b) => b.score - a.score);
    out.push({
      reportType,
      docs: scored.slice(0, TOP_HITS_PER_TYPE),
      highestRelevance: scored[0]!.score,
      totalDocsForType: scored.length,
    });
  }
  return out;
}

/* ── "Used in" lookup ───────────────────────────────────────── */

/**
 * Walks the supplied reports array and returns the unique report types
 * that include `docId` (via documentRefs.userIncluded === true). Reports
 * with stale inline `documents` arrays still resolve correctly through
 * `getReportDocuments`. Output order matches the input report types' order
 * of first appearance for a stable badge layout.
 *
 * Semantics: "which generated reports already cited this doc" — i.e. the
 * audit-trail of report-generation history. For the intrinsic "which
 * report types is this doc applicable to" question (the headline tag in
 * the Documents tab), use `applicableReportTypes` instead — see the DRT
 * pack (row 0.99199) for the bug this distinction was created to fix.
 */
export function usedInReportTypes(
  docId: string,
  reports: SiteRagReport[],
  site?: SiteEntity,
): ReportTypeId[] {
  const seen = new Set<ReportTypeId>();
  const order: ReportTypeId[] = [];
  for (const r of reports) {
    const docs = getReportDocuments(r, site);
    if (docs.some(d => d.id === docId)) {
      const t = (r.reportType ?? 'feasibility') as ReportTypeId;
      if (!seen.has(t)) {
        seen.add(t);
        order.push(t);
      }
    }
  }
  return order;
}

/* ── UXS-7 — body-cited lookup (ground truth for "Cited by" badge) ── */

/**
 * UXS-7 — returns the unique report types whose NARRATIVE actually
 * cites `docId` (via `[Ref N]` body tokens resolved through SED lineage,
 * OR via `ReportClaim.sources` carrying a `doc:<id>` prefix). Composes
 * `computeBodyCitedDocIds` (RCO-1) per report.
 *
 * Semantics — strictly stronger than `usedInReportTypes`:
 *
 *   - `usedInReportTypes`  → "user toggled this doc on in the rerun
 *                            wizard" (intent stamp, can be over-broad)
 *   - `bodyCitedInReportTypes` → "the report's narrative actually leans
 *                            on this doc" (fact, ground truth)
 *
 * Per UXS-7 + HARD RULE 10, the DocumentLibrary "CITED BY" badge MUST
 * reflect body citations, not user intent. If a doc has zero body
 * citations but the user toggled it on in the wizard, it should NOT
 * carry a CITED BY badge.
 *
 * The `sed` argument is optional: when omitted, only Path B
 * (`ReportClaim.sources` with `doc:` prefix) fires. CSE shipped row
 * 0.99237 which populates `report.claims[]` going forward, so on
 * fresh reports the Path B set is sufficient. Path A is the legacy
 * `[Ref N]` superscript path and only resolves when the SED is
 * available.
 *
 * Pure / deterministic. Output order matches the input reports' first-
 * appearance order for a stable badge layout.
 */
export function bodyCitedInReportTypes(
  docId: string,
  reports: SiteRagReport[],
  sed?: SiteEvidenceDocument,
): ReportTypeId[] {
  const seen = new Set<ReportTypeId>();
  const order: ReportTypeId[] = [];
  for (const r of reports) {
    const cited = computeBodyCitedDocIds(r, sed, { liveDocs: r.documents ?? [] });
    if (cited.has(docId)) {
      const t = (r.reportType ?? 'feasibility') as ReportTypeId;
      if (!seen.has(t)) {
        seen.add(t);
        order.push(t);
      }
    }
  }
  return order;
}

/* ── "Applicable to" lookup ─────────────────────────────────── */

/** Minimum relevance score for a report type to be reported as applicable. */
export const APPLICABLE_RELEVANCE_THRESHOLD = 0.3;

/**
 * Returns the set of report types this document is INTRINSICALLY applicable
 * to, based on the classifier's per-type relevance scores (`doc.relevance`).
 *
 * Distinct from `usedInReportTypes` — that one queries report-generation
 * history (`report.documentRefs[].userIncluded`); this one queries the
 * classifier output. The DocumentLibrary "Applicable to" pill uses this
 * function; the planned "Cited by" row uses the other. See the DRT pack
 * (row 0.99199) for the bug this distinction was created to fix — on
 * single-Feasibility-report sites every doc was rendering "USED IN:
 * FEASIBILITY" because the surface read `documentRefs` (mass-stamped
 * via the `siteRagService` fallback) instead of `doc.relevance`.
 *
 * Sort order is alphabetical for stable snapshot tests and deterministic
 * UI ordering. Empty result when:
 *   - `doc.relevance` is missing (legacy upload pre-classifier), OR
 *   - every score is below `threshold` (0.3 by default).
 * Callers should render an explicit "Not yet classified" empty-state when
 * `[]` is returned, rather than hiding the row.
 *
 * DRT-AUDIT outcomes (recorded 2026-05-18, in-worktree audit):
 *   1. `git grep "applicableReportTypes"` returns the export here plus
 *      two call sites in DocumentLibrary.tsx (per-row pill + filter
 *      pre-resolver).
 *   2. `git grep "Used in:"` in DocumentLibrary.tsx returns ZERO visible
 *      heading strings — only a code comment remains. Both pill rows
 *      now read "Applicable to:" / "Cited by:".
 *   3. `git grep "usedInReportTypes(doc.id, usageContext"` returns ZERO
 *      matches — the old per-row call site is fully replaced.
 *   4. `npx tsc --noEmit` clean on `src/` (zero errors introduced).
 *   5. `npx vitest run` — 617 test files, 6315 passing (+ 16 new from
 *      DRT-1 / DRT-4 / DRT-6), 0 regressions.
 *   6. Live-prod browser probe deferred to reviewer — agent has no
 *      browser context. Per the DRT pack §4: open
 *      `https://datacrate.io` → Redhead → Documents tab → verify
 *      Phase I ESA → APPLICABLE TO: ENVIRONMENTAL, Water Rights Cert
 *      → APPLICABLE TO: WATER RESOURCE, etc.
 *   7. Performance: O(report-types) per doc — 81 docs × 6 types ≈ 486
 *      ops per Documents-tab render. Negligible.
 */
export function applicableReportTypes(
  doc: Pick<SiteDocument, 'relevance'>,
  threshold: number = APPLICABLE_RELEVANCE_THRESHOLD,
): ReportTypeId[] {
  const relevance = doc.relevance ?? {};
  const out: ReportTypeId[] = [];
  for (const [reportType, score] of Object.entries(relevance)) {
    if (typeof score === 'number' && Number.isFinite(score) && score >= threshold) {
      out.push(reportType as ReportTypeId);
    }
  }
  return out.sort();
}

/* ── Per-report doc summary (chip + comparison row) ─────────────── */

export interface ReportDocSummary {
  reportId: string;
  totalDocs: number;
  nbPriority: number;
  /** Top-N preview for hover cards / inline summaries. */
  preview: Array<{ doc: SiteDocument; score: number | null }>;
  /** Resolved doc list for downstream overlap math. */
  docs: SiteDocument[];
}

/**
 * Build a per-report doc summary suitable for the lineage chip + the
 * comparison view's basis row. `preview` is sorted by relevance for the
 * report's own type when classifier scores exist; otherwise by priority
 * flag, then alphabetical.
 */
export function summarizeReportDocs(
  report: SiteRagReport,
  site: SiteEntity | undefined,
): ReportDocSummary {
  const docs = getReportDocuments(report, site);
  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  const scored = docs.map(d => ({ doc: d, score: d.relevance?.[reportType] ?? null }));
  scored.sort((a, b) => {
    const sa = typeof a.score === 'number' ? a.score : -1;
    const sb = typeof b.score === 'number' ? b.score : -1;
    if (sb !== sa) return sb - sa;
    if ((a.doc.priority ? 1 : 0) !== (b.doc.priority ? 1 : 0)) {
      return (b.doc.priority ? 1 : 0) - (a.doc.priority ? 1 : 0);
    }
    return a.doc.fileName.localeCompare(b.doc.fileName);
  });
  return {
    reportId: report.id,
    totalDocs: docs.length,
    nbPriority: docs.filter(d => d.priority).length,
    preview: scored.slice(0, HOVER_PREVIEW_LIMIT),
    docs,
  };
}

/* ── ComparisonView Document basis row ──────────────────────────── */

export interface DocumentBasisCell {
  reportId: string;
  totalDocs: number;
  nbPriority: number;
  /** Doc IDs included in this report, used for overlap math. */
  docIds: Set<string>;
  /**
   * Per-other-column overlap. Maps the OTHER report's id to the count
   * of doc ids that appear in BOTH columns. The view typically renders
   * the strongest overlap (e.g. "8 shared with col 2") plus a "unique"
   * count derived as `totalDocs - max(overlap)`.
   */
  overlapByReportId: Record<string, number>;
  /** Docs in this column not present in any other column. */
  uniqueCount: number;
}

/**
 * Compute the Document basis row for a list of reports being compared
 * side-by-side. Overlap is symmetric, but we store it keyed by the
 * other report's id so the renderer can choose how to label "shared
 * with col N" without re-computing.
 *
 * PH-B1.2 — the second parameter accepts either a single `SiteEntity`
 * (legacy single-site comparisons) OR a resolver function
 * `(report) => SiteEntity | undefined` so cross-site comparisons can
 * look up each report's OWN site's documents rather than collapsing
 * all reports onto the first selected site.
 */
export function documentBasisRow(
  reports: SiteRagReport[],
  siteOrResolver:
    | SiteEntity
    | undefined
    | ((report: SiteRagReport) => SiteEntity | undefined),
): DocumentBasisCell[] {
  const resolveSite = (r: SiteRagReport): SiteEntity | undefined =>
    typeof siteOrResolver === 'function' ? siteOrResolver(r) : siteOrResolver;
  const summaries = reports.map(r => summarizeReportDocs(r, resolveSite(r)));
  const cells: DocumentBasisCell[] = summaries.map(s => ({
    reportId: s.reportId,
    totalDocs: s.totalDocs,
    nbPriority: s.nbPriority,
    docIds: new Set(s.docs.map(d => d.id)),
    overlapByReportId: {},
    uniqueCount: 0,
  }));

  // Pairwise overlap (i, j) for i ≠ j.
  for (let i = 0; i < cells.length; i++) {
    const a = cells[i]!;
    for (let j = 0; j < cells.length; j++) {
      if (i === j) continue;
      const b = cells[j]!;
      let overlap = 0;
      for (const id of a.docIds) {
        if (b.docIds.has(id)) overlap++;
      }
      a.overlapByReportId[b.reportId] = overlap;
    }
  }

  // Unique = ids present in `a` but in NO other column.
  for (let i = 0; i < cells.length; i++) {
    const a = cells[i]!;
    let unique = 0;
    outer: for (const id of a.docIds) {
      for (let j = 0; j < cells.length; j++) {
        if (i === j) continue;
        if (cells[j]!.docIds.has(id)) continue outer;
      }
      unique++;
    }
    a.uniqueCount = unique;
  }

  return cells;
}
